import { z } from 'zod'
import {
  docId,
  isoDate,
  isoTimestamp,
  money,
  reconciliationStatusEnum,
} from './common.js'

/**
 * `reconciliationPeriods` and `creditCardStatements` — REQUIREMENTS.md §5.5 and
 * §5.5a. Unused until Phase 5.
 *
 * The statement is the source of truth for three things the packet depends on:
 * the order items appear in, the vendor spelling the business office
 * reconciles against, and the total they cross-check.
 */

export const reconciliationPeriodSchema = z.object({
  id: docId.optional(),
  userId: z.string().min(1),

  /** 25th of the previous month through the 24th of the current month. */
  cycleStart: isoDate,
  cycleEnd: isoDate,
  statementDate: isoDate.nullable().default(null),
  statementId: docId.nullable().default(null),

  /**
   * Packet order, set automatically from the statement's line order. This is
   * the field that solves the manual-reordering problem (§5.5 design note).
   * Drag-and-drop remains available as a fallback when no statement is ingested.
   */
  orderedTransactionIds: z.array(docId).default([]),
  /** Statement lines with no matching capture; become packet tombstones. */
  unmatchedStatementLineIds: z.array(z.string()).default([]),

  status: reconciliationStatusEnum.default('open'),
  packetPdfUrl: z.string().nullable().default(null),
  packetXlsxUrl: z.string().nullable().default(null),
  submittedAt: isoTimestamp.nullable().default(null),

  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
})

export type ReconciliationPeriod = z.infer<typeof reconciliationPeriodSchema>

/** One extracted line from the paper statement (§5.5a). */
export const statementLineSchema = z.object({
  /** 1-indexed position on the statement. Becomes the packet item number. */
  lineIndex: z.number().int().min(1),
  postedDate: isoDate,
  transactionDate: isoDate,
  /**
   * Vendor name exactly as the statement prints it. This — not the receipt's
   * vendor — is what appears on the log sheet (§7.2, §8.1).
   */
  merchantAsPrinted: z.string().trim().min(1).max(200),
  /** Signed; lines marked `CR` extract as negative (§6.6). */
  amount: money,
  matchedTransactionId: docId.nullable().default(null),
  matchConfidence: z.number().min(0).max(1).default(0),
  userConfirmed: z.boolean().default(false),
})

export type StatementLine = z.infer<typeof statementLineSchema>

export const creditCardStatementSchema = z.object({
  id: docId.optional(),
  userId: z.string().min(1),
  reconciliationPeriodId: docId,

  /** One per photographed page, in capture order (§6.6 multi-page stitching). */
  originalImageUrls: z.array(z.string()).min(1),
  renderedPdfUrl: z.string().nullable().default(null),

  /** Extracted, then sanity-checked against the user profile. */
  cardholderName: z.string().trim().max(120).nullable().default(null),
  statementCycleStart: isoDate.nullable().default(null),
  statementCycleEnd: isoDate.nullable().default(null),
  /** Cross-checked against the packet total before generation (§7.2 step 6). */
  statementTotal: money.nullable().default(null),

  lines: z.array(statementLineSchema).default([]),

  extractionModel: z.string().max(120).nullable().default(null),
  extractionConfidence: z.number().min(0).max(1).nullable().default(null),
  photographedAt: isoTimestamp,
  processedAt: isoTimestamp.nullable().default(null),
})

export type CreditCardStatement = z.infer<typeof creditCardStatementSchema>

/** Log sheet rows per page — fixed by the district form (§8.1). */
export const LOG_SHEET_ROWS_PER_PAGE = 10
